import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iconPlusButton, iconSearchButton } from '../../assets';
import { useTheme } from '../../theme';

export interface FilterOption {
  value: string;
  label: string;
}

export interface QuestionBankFilterProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  difficultyOptions: FilterOption[];
  difficultyValue: string;
  onDifficultyChange: (value: string) => void;
  difficultyLabel?: string;
  categoryOptions: FilterOption[];
  categoryValue: string;
  onCategoryChange: (value: string) => void;
  categoryLabel?: string;
  onCreateQuestion?: () => void;
  addQuestionLabel?: string;
  addQuestionAriaLabel?: string;
  /** Identificador de teste para o input de busca */
  dataE2eSearchInput?: string;
  /** Identificador de teste para o botão de criar questão */
  dataE2eAddButton?: string;
}

function ChevronDown({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path d="M7 10l5 5 5-5z" />
    </svg>
  );
}

export function QuestionBankFilter({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  difficultyOptions,
  difficultyValue,
  onDifficultyChange,
  difficultyLabel,
  categoryOptions,
  categoryValue,
  onCategoryChange,
  categoryLabel,
  onCreateQuestion,
  addQuestionLabel,
  addQuestionAriaLabel,
  dataE2eSearchInput,
  dataE2eAddButton,
}: QuestionBankFilterProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [difficultyOpen, setDifficultyOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  };

  const filterLayout = theme.layout?.questionBankFilter;
  const boxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'stretch',
    flex: `1 1 ${filterLayout?.boxFlexBasis ?? 280}px`,
    minWidth: filterLayout?.boxMinWidth ?? 200,
    maxWidth: filterLayout?.boxMaxWidth ?? 720,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.filterBox.border}`,
    borderRadius: theme.shape.borderRadius.md,
    backgroundColor: theme.palette.filterBox.background,
    overflow: 'visible',
  };

  const searchWrapperStyle: React.CSSProperties = {
    position: 'relative',
    flex: 1,
    minWidth: filterLayout?.searchMinWidth ?? 90,
    margin: theme.spacing.sm,
    backgroundColor: theme.palette.searchInFilter.background,
    borderRadius: theme.shape.borderRadius.md,
    overflow: 'hidden',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    minHeight: filterLayout?.searchMinHeight ?? 40,
    padding: `${theme.spacing.inputY} ${theme.spacing.md} ${theme.spacing.inputY} 2.5rem`,
    fontSize: theme.typography.fontSize.base,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.searchInFilter.background,
    border: 'none',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const iconSpanStyle: React.CSSProperties = {
    position: 'absolute',
    left: theme.spacing.md,
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.searchInFilter.icon,
    pointerEvents: 'none',
  };

  const filterButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    margin: theme.spacing.sm,
    width: filterLayout?.filterButtonWidth ?? 140,
    minWidth: filterLayout?.filterButtonWidth ?? 140,
    height: filterLayout?.filterButtonHeight ?? 40,
    borderRadius: theme.shape.borderRadius.sm,
    backgroundColor: theme.palette.filterBox.background,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.button.filterBorder}`,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.palette.button.filterText,
    cursor: 'pointer',
    boxSizing: 'border-box',
  };

  const filterButtonLabelStyle: React.CSSProperties = {
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const actionButtonStyle: React.CSSProperties = onCreateQuestion
    ? {
        fontSize: theme.typography.fontSize.base,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius.sm2,
        border: 'none',
        cursor: 'pointer',
        minWidth: filterLayout?.addButtonMinWidth ?? 180,
        width: filterLayout?.addButtonMinWidth ?? 180,
        height: filterLayout?.addButtonHeight ?? 42,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.sm,
        marginLeft: theme.spacing.lg,
      }
    : {};

  const dropdownListStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 'calc(100% + 4px)',
    minWidth: '100%',
    padding: theme.spacing.xs,
    margin: 0,
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.sm,
    boxShadow: theme.palette.createCourse?.dropdownBoxShadow ?? '0 4px 12px rgba(0,0,0,0.1)',
    listStyle: 'none',
    zIndex: 11,
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 10,
  };

  const searchPlaceholderText = searchPlaceholder ?? t('questionBank.search_placeholder');
  const searchAriaText = searchAriaLabel ?? t('questionBank.search_aria');
  const difficultyLabelText = difficultyLabel ?? t('questionBank.all_difficulties');
  const categoryLabelText = categoryLabel ?? t('questionBank.all_categories');
  const addLabelText = addQuestionLabel ?? t('questionBank.add_question');
  const addAriaText = addQuestionAriaLabel ?? t('questionBank.add_question_aria');

  const getDifficultyDisplayLabel = () => {
    const opt = difficultyOptions.find(o => o.value === difficultyValue);
    return opt ? opt.label : difficultyLabelText;
  };

  const getCategoryDisplayLabel = () => {
    const opt = categoryOptions.find(o => o.value === categoryValue);
    return opt ? opt.label : categoryLabelText;
  };

  const searchInputClassName = 'questionbank-search-input';
  const btnClassName = 'questionbank-btn';

  const renderDropdownOption = (
    opt: FilterOption,
    selected: boolean,
    onSelect: () => void
  ) => (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: 'block',
        width: '100%',
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        textAlign: 'left',
        backgroundColor: selected ? theme.palette.primary.main : theme.palette.background.paper,
        color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
        border: 'none',
        borderRadius: theme.shape.borderRadius.sm,
        fontSize: theme.typography.fontSize.sm,
        fontFamily: theme.typography.fontFamily,
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        if (!selected) e.currentTarget.style.backgroundColor = theme.palette.background.default;
      }}
      onMouseLeave={e => {
        if (!selected) e.currentTarget.style.backgroundColor = theme.palette.background.paper;
      }}
    >
      {opt.label}
    </button>
  );

  return (
    <div style={containerStyle} role="search">
      <style>{`.${searchInputClassName}::placeholder { color: ${theme.palette.searchInFilter.placeholder}; }`}</style>

      <div style={boxStyle}>
        <div style={searchWrapperStyle}>
          <span style={iconSpanStyle} aria-hidden>
            <img
              src={iconSearchButton}
              alt=""
              width={theme.palette.createCourse?.iconSizeSmall ?? 16}
              height={theme.palette.createCourse?.iconSizeSmall ?? 16}
            />
          </span>
          <input
            type="text"
            value={searchValue}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={searchPlaceholderText}
            style={searchInputStyle}
            className={searchInputClassName}
            aria-label={searchAriaText}
            data-e2e={dataE2eSearchInput}
          />
        </div>

        {/* All Difficulties (first) */}
        <div style={{ position: 'relative', zIndex: difficultyOpen ? 12 : undefined }}>
          <button
            type="button"
            style={filterButtonStyle}
            onClick={() => {
              setDifficultyOpen(o => !o);
              setCategoryOpen(false);
            }}
            aria-expanded={difficultyOpen}
            aria-haspopup="listbox"
            aria-label={difficultyLabelText}
          >
            <span style={filterButtonLabelStyle}>{getDifficultyDisplayLabel()}</span>
            <ChevronDown size={theme.palette.createCourse?.chevronSize ?? 14} />
          </button>
          {difficultyOpen && (
            <>
              <div role="presentation" style={overlayStyle} onClick={() => setDifficultyOpen(false)} />
              <ul role="listbox" aria-label={difficultyLabelText} style={dropdownListStyle}>
                {difficultyOptions.map(opt => (
                  <li key={opt.value} role="option" aria-selected={opt.value === difficultyValue} style={{ marginBottom: '2px' }}>
                    {renderDropdownOption(opt, opt.value === difficultyValue, () => {
                      onDifficultyChange(opt.value);
                      setDifficultyOpen(false);
                    })}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* All Categories (second) */}
        <div style={{ position: 'relative', zIndex: categoryOpen ? 12 : undefined }}>
          <button
            type="button"
            style={filterButtonStyle}
            onClick={() => {
              setCategoryOpen(o => !o);
              setDifficultyOpen(false);
            }}
            aria-expanded={categoryOpen}
            aria-haspopup="listbox"
            aria-label={categoryLabelText}
          >
            <span style={filterButtonLabelStyle}>{getCategoryDisplayLabel()}</span>
            <ChevronDown size={theme.palette.createCourse?.chevronSize ?? 14} />
          </button>
          {categoryOpen && (
            <>
              <div role="presentation" style={overlayStyle} onClick={() => setCategoryOpen(false)} />
              <ul role="listbox" aria-label={categoryLabelText} style={dropdownListStyle}>
                {categoryOptions.map(opt => (
                  <li key={opt.value} role="option" aria-selected={opt.value === categoryValue} style={{ marginBottom: '2px' }}>
                    {renderDropdownOption(opt, opt.value === categoryValue, () => {
                      onCategoryChange(opt.value);
                      setCategoryOpen(false);
                    })}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {onCreateQuestion && (
        <button
          type="button"
          className={btnClassName}
          onClick={onCreateQuestion}
          style={actionButtonStyle}
          aria-label={addAriaText}
          data-e2e={dataE2eAddButton}
        >
          <img
            src={iconPlusButton}
            alt=""
            width={theme.palette.createCourse?.iconSizeSmall ?? 16}
            height={10}
          />
          {addLabelText}
        </button>
      )}
    </div>
  );
}
